import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, FileText, Building2, Package, Calendar, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatDate, maskCNPJ } from '@/lib/format';
import { toast } from 'sonner';
import axios from 'axios';

const STATUS_LABELS = {
  OPEN: 'Aberto',
  WAITING_RECEIPT: 'Aguardando Recebimento',
  IN_PROGRESS: 'Em Andamento',
  WAITING_CUSTOMER: 'Aguardando Cliente',
  WAITING_PARTS: 'Aguardando Peças',
  IN_MAINTENANCE: 'Em Manutenção',
  SENT_TO_CLIENT: 'Enviado ao Cliente',
  FINALIZED: 'Finalizado',
  CANCELLED: 'Cancelado',
};

const STATUS_COLORS = {
  OPEN: 'bg-blue-100 text-blue-700',
  WAITING_RECEIPT: 'bg-amber-100 text-amber-700',
  IN_PROGRESS: 'bg-yellow-100 text-yellow-700',
  WAITING_CUSTOMER: 'bg-orange-100 text-orange-700',
  WAITING_PARTS: 'bg-purple-100 text-purple-700',
  IN_MAINTENANCE: 'bg-indigo-100 text-indigo-700',
  SENT_TO_CLIENT: 'bg-cyan-100 text-cyan-700',
  FINALIZED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-slate-100 text-slate-700',
};

export default function PublicTicketListPage() {
  const { cnpj } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTickets();
  }, [cnpj]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const headers = {};
      if (location.state?.recaptchaToken) {
        headers['x-recaptcha-token'] = location.state.recaptchaToken;
      }
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/public/tickets/by-cnpj/${cnpj}`,
        { headers }
      );
      
      setTickets(response.data);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      const errorMessage = err.response?.data?.message || 'Não foi possível buscar os tickets. Verifique o CNPJ e tente novamente.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Consultando tickets...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-2xl mx-auto">
            <Card className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Nenhum ticket encontrado</h2>
              <p className="text-slate-600 mb-6">{error}</p>
              <Button onClick={() => navigate('/consulta')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar para consulta
              </Button>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  const companyName = tickets[0]?.companyName || 'Empresa';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center">
                <Building2 className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Seus Tickets</h1>
                <p className="text-sm text-slate-600">CNPJ: {maskCNPJ(cnpj)}</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate('/consulta')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Nova Consulta
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto">
          {/* Info */}
          <div className="mb-6">
            <p className="text-slate-600">
              <strong>{tickets.length}</strong> {tickets.length === 1 ? 'ticket encontrado' : 'tickets encontrados'} • 
              Empresa: <strong>{companyName}</strong>
            </p>
          </div>

          {/* Tickets List */}
          <div className="space-y-4">
            {tickets.map((ticket) => (
              <Card key={ticket.protocol} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-slate-900 mb-1">{ticket.protocol}</h3>
                      <p className="text-sm text-slate-600 line-clamp-2">{ticket.problemDescription}</p>
                    </div>
                  </div>
                  <Badge className={STATUS_COLORS[ticket.status]}>
                    {STATUS_LABELS[ticket.status]}
                  </Badge>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Package className="w-4 h-4" />
                    <span>{ticket.productName}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(ticket.openedAt)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Building2 className="w-4 h-4" />
                    <span>{ticket.city}/{ticket.state}</span>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Link to={`/consulta/ticket/${ticket.protocol}`}>
                    <Button variant="outline" size="sm">
                      Ver Detalhes
                    </Button>
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
